import { Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormID } from '@finxone-platform/form-action';
import { Store } from '@ngxs/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, Observable, of, take, tap } from 'rxjs';
import { SetFormActionWithId } from '../../actions/form-submission.action';
import { FormSessionService } from '../../services/forms-session/forms-session.service';
import { FormActionState } from '../../state/form-submision.state';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { ProfileState } from '../../state/user-profile.state';

export const restrictedFieldsToPurge = ['password', 'confirm_password', 'confirmPassword'];

export interface FormSessionRequest {
  formData: Record<string, any>;
  deviceInfo: Record<string, any>;
  formName: FormID | string;
  isCompleted?: boolean;
  formSessionId?: string;
  userId?: string;
}

export interface FormSessionResponse {
  formSessionId: string;
}

export function submitFormInProgress(parentFormId: FormID | undefined, injector: Injector) {
  const store = injector.get(Store);
  const formService = injector.get(FormSessionService);
  const deviceService = injector.get(DeviceDetectorService);
  const projectConfig = store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig;
  const userId = store.selectSnapshot(ProfileState.getProfile)?.id;

  if (!parentFormId) return;
  if (!projectConfig?.formProgress?.formId?.includes(parentFormId)) return;

  const selectedFormData = store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId));
  const payload = buildFormSessionRequestPayload(selectedFormData, deviceService, parentFormId, userId);
  submitFormProgressRequest(formService, store, payload, parentFormId);
}

function submitFormProgressRequest(
  formService: FormSessionService,
  store: Store,
  payload: FormSessionRequest,
  parentFormId: FormID | string,
) {
  restrictedFieldsToPurge.forEach((field) => delete payload.formData?.[field]);

  formService
    .formInProgress(payload)
    .pipe(take(1))
    .subscribe({
      next: (response: FormSessionResponse) => {
        updateFormState(response, store, parentFormId);
      },
      error: (err) => {
        console.error('Error sending form progress:', err);
      },
    });
}

function updateFormState(response: FormSessionResponse, store: Store, parentFormId: string) {
  const existingFormState = store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId));
  if (existingFormState?.formData?.formSessionId) return;

  store.dispatch(
    new SetFormActionWithId(
      {
        type: '',
        formData: {
          ...existingFormState?.formData,
          formSessionId: response?.formSessionId,
        },
      },
      parentFormId,
    ),
  );
}

export function setFormCompletionStatus(
  parentFormId: FormID | undefined,
  injector: Injector,
): Observable<any> {
  const store = injector.get(Store);
  const formService = injector.get(FormSessionService);
  const deviceService = injector.get(DeviceDetectorService);
  const projectConfig = store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig;

  if (!parentFormId) return of(null);
  if (!projectConfig?.formProgress?.formId?.includes(parentFormId)) return of(null);

  const selectedFormData = store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId));
  const userId = store.selectSnapshot(ProfileState.getProfile)?.id;

  if (!selectedFormData?.formData?.formSessionId) return of(null);

  const payload = buildFormSessionRequestPayload(selectedFormData, deviceService, parentFormId, userId, true);

  return formService.updateFormCompletionState(payload).pipe(take(1));
}

function buildFormSessionRequestPayload(
  selectedFormData: { formData?: Record<string, any> } | undefined,
  deviceService: DeviceDetectorService,
  parentFormId: FormID | string,
  userId?: string | undefined,
  isCompleted?: boolean | undefined,
): FormSessionRequest {
  const deviceInfo = deviceService.getDeviceInfo() || {};

  const cleanedFormData = { ...selectedFormData?.formData };
  delete cleanedFormData?.['formSessionId'];

  return {
    formSessionId: selectedFormData?.formData?.['formSessionId'],
    formData: cleanedFormData,
    deviceInfo,
    formName: parentFormId,
    isCompleted: isCompleted,
    userId: userId,
  };
}

/**
 * Fetches the user's in-progress form data using the formSessionId from the URL
 * and updates the application state with this data.
 *
 * This ensures that if a user returns to a form they started earlier,
 * all previously entered data is restored, allowing them to continue
 * from where they left off.
 */
export function getFormProgressDataAndSaveInState(
  parentFormId: FormID | undefined,
  injector: Injector,
): Observable<any> {
  const store = injector.get(Store);
  const activeRoute = injector.get(ActivatedRoute);
  const formService = injector.get(FormSessionService);
  const projectConfig = store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig;

  if (!parentFormId || !projectConfig?.formProgress?.formId?.includes(parentFormId)) return of(null);

  const formSessionId = activeRoute.snapshot?.queryParamMap?.get('formSessionId') || '';
  const existingFormStateData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(parentFormId),
  )?.formData;

  if (!formSessionId || existingFormStateData?.formSessionId === formSessionId) return of(null);

  // Call the API to fetch form progress data only once if a formSessionId exists in the query parameters.
  // If its already present in state, no need to fetch again
  return formService.getFormProgressData(formSessionId).pipe(
    take(1),
    tap((response) => {
      const filteredResponse = filterOutVerificationRelatedKeys(response.formData);
      // Save the fetched data in the state
      store.dispatch(
        new SetFormActionWithId(
          {
            type: '',
            formData: { ...filteredResponse, formSessionId: response?.formSessionId },
          },
          parentFormId,
        ),
      );
    }),
    catchError((_err) => {
      throw _err;
    }),
  );
}

function filterOutVerificationRelatedKeys(formData: Record<string, string>): Record<string, string> {
  const keysToFilter = new Set([
    'bankDetailsVerificationTokens',
    'isEmailVerified',
    'isPhoneVerified',
    'verifiedEmail',
    'verifiedPhone',
    'verifiedCountryCode',
    'emailVerifiedAt',
    'phoneVerifiedAt',
    'emailVerificationToken',
    'phoneVerificationToken',
    'copVerifiedAt',
  ]);
  const filteredData: Record<string, string> = {};

  for (const key in formData) {
    if (!keysToFilter.has(key)) {
      filteredData[key] = formData[key];
    }
  }

  return filteredData;
}
